import { useAuth } from '@contexts/AuthContext';
import { useScrollOverflow } from '@hooks/useScrollOverflow';
import {
  isInSeason,
  type League,
  leagueToLeagueKey,
  SUPPORTED_LEAGUES,
} from '@parlay-picker/shared/league';
import { type ReactElement, useRef } from 'react';

interface LeagueSelectorProps {
  currentLeague: League;
}

export function LeagueSelector({ currentLeague }: LeagueSelectorProps): ReactElement {
  const { updatePreferences } = useAuth();
  const leagueSelectorRef = useRef<HTMLDivElement>(null);
  const { canScrollRight: leagueSelectorOverflows } = useScrollOverflow(leagueSelectorRef);

  return (
    <div className="mb-4">
      <p className="text-xs text-theme-text-tertiary mb-1.5">League</p>
      <div className="relative">
        {leagueSelectorOverflows && (
          <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-theme-surface to-transparent z-10 pointer-events-none" />
        )}
        <div ref={leagueSelectorRef} className="flex gap-1.5 pb-0.5 overflow-x-auto scrollbar-none">
          {[...SUPPORTED_LEAGUES]
            .sort((a, b) => {
              const aIn = isInSeason(leagueToLeagueKey(a));
              const bIn = isInSeason(leagueToLeagueKey(b));
              if (aIn === bIn) return 0;
              return aIn ? -1 : 1;
            })
            .map((league) => {
              const inSeason = isInSeason(leagueToLeagueKey(league));
              return (
                <button
                  key={league}
                  type="button"
                  disabled={!inSeason}
                  onClick={() => {
                    if (inSeason && league !== currentLeague) {
                      updatePreferences({ preferredLeague: leagueToLeagueKey(league) });
                    }
                  }}
                  className={`text-xs px-3 py-1.5 rounded-full font-medium transition-all whitespace-nowrap shrink-0 ${
                    !inSeason
                      ? 'bg-theme-surface-hover text-theme-text-tertiary opacity-50 cursor-not-allowed'
                      : currentLeague === league
                        ? 'bg-theme-primary-fill text-theme-on-primary-fill ring-1 ring-theme-primary/50'
                        : 'bg-theme-surface-hover text-theme-text-secondary hover:text-theme-text hover:bg-theme-border'
                  }`}
                >
                  {league}
                  {!inSeason && <span className="ml-1 text-[10px]">(off)</span>}
                </button>
              );
            })}
        </div>
      </div>
    </div>
  );
}
